import clsx from "clsx";
import { DemographicIcon, getDemographicByUnitId } from "common";
import { sortBy } from "lodash";
import React from "react";
import { Event, Experience, Permission, Resource, Timeslot, Unit } from "types";
import { IsGranted } from "../../Common/IsGranted";
import { NoTranslate } from "../../Common/NoTranslate";
import { useResource } from "@/hooks/useResource";
import { isDefaultDemographic } from "@/models/DemographicUnit";
import { getAggregatedResourceUsages } from "@/models/Event";
import { getBookedEquipmentUnits, isEquipmentTimeslot } from "@/models/Timeslot";

export const DemographicPopoverList = ({ timeslot, experience }: { timeslot: Timeslot; experience: Experience }) => {
    if (isEquipmentTimeslot(timeslot)) {
        return <EquipmentPopoverList timeslot={timeslot} />;
    }

    const demographics = timeslot.purchasedUnits?.demographics;
    const total = timeslot.capacity.reserved;

    if (!demographics?.length) {
        return null;
    }

    const data = demographics.map((demographic) => {
        const catalogItem = getDemographicByUnitId(experience, demographic.id);

        return {
            ...demographic,
            name: catalogItem?.name,
            sequence: catalogItem?.sequence,
        };
    });

    return (
        <IsGranted permissions={Permission.DEMOGRAPHIC_LIST_VIEW}>
            <span className="flex flex-col font-light text-gray-darker">
                {sortBy(data, "sequence").map((demographic) =>
                    demographic.count > 0 ? (
                        <DemographicItem
                            key={demographic.id}
                            id={demographic.id}
                            name={demographic.name}
                            count={demographic.count}
                        />
                    ) : null,
                )}
            </span>
            <hr className="text-gray-light" />
            <div className="text-bold flex justify-between">
                <span>Total</span> <NoTranslate>{total}</NoTranslate>
            </div>
        </IsGranted>
    );
};

const EquipmentPopoverList = ({ timeslot }: { timeslot: Timeslot }) => {
    const { data: event } = useResource<Event>(["events", timeslot.event?.id]);
    const total = getBookedEquipmentUnits(timeslot);

    const data = getAggregatedResourceUsages(event?.resourceUsages);

    if (data.length === 0) {
        return null;
    }

    return (
        <IsGranted permissions={Permission.MODIFY_RESOURCES_EQUIPMENT}>
            <span className="flex flex-col font-light text-gray-darker">
                {data.map((usage) => (
                    <EquipmentReservedItem key={usage.resource.id} resourceId={usage.resource.id} count={usage.count} />
                ))}
            </span>
            <hr className="text-gray-light" />
            <div className="text-bold flex justify-between">
                <span>Total</span> <NoTranslate>{total}</NoTranslate>
            </div>
        </IsGranted>
    );
};

const EquipmentReservedItem = ({ resourceId, count }: { resourceId: ID; count: number }) => {
    const { data: resource } = useResource<Resource>(["resources", resourceId]);
    return resource?.name ? (
        <div className="flex justify-between">
            <span className="no-translate">{resource.name}</span>
            <NoTranslate>{count}</NoTranslate>
        </div>
    ) : null;
};

const DemographicItem = ({ name, id, count }: { name?: string; id: string; count: number }) => {
    // call units endpoint only in case demographic was removed from the experience, and name is unavailable
    const { data: demographicUnit } = useResource<Unit>(["units", id], null, { enabled: !name });
    const demographicName = name ?? demographicUnit?.name;
    const shouldTranslate = isDefaultDemographic(demographicName);

    return demographicName ? (
        <div className="flex justify-between">
            <span>
                <DemographicIcon name={demographicName} />{" "}
                <span className={clsx(!shouldTranslate && "no-translate")}>{demographicName}</span>
            </span>
            <NoTranslate>{count}</NoTranslate>
        </div>
    ) : null;
};
